import {
  type RefObject,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react"

import {
  type StripLayout,
  type StripMetrics,
  resolveStripLayout
} from "../helpers/project-tab-strip-layout.ts"

/** The strip renders hidden for these passes so natural widths can be read. */
type MeasureStep = "full" | "icons"

interface Args<Key extends string> {
  tabs: Array<{ value: Key; label: string }>
  activeTab: Key | null
  /** the translated "More", which is also the key its probe is measured under */
  moreLabel: string
}

interface Result<Key extends string> extends StripLayout<Key> {
  /** put on the strip: the element that is measured and observed */
  stripRef: RefObject<HTMLDivElement | null>
  /** put on the off-flow probe container that sizes the "More" button */
  probesRef: RefObject<HTMLDivElement | null>
  /** true while either hidden measuring pass is rendering */
  measuring: boolean
  /**
   * True during the second pass only. The strip must render its labels
   * collapsed for it, or the pass measures the labelled widths again and the
   * collapsed rung never becomes reachable.
   */
  measuringCollapsed: boolean
}

/**
 * Measures a tab strip once, then decides its layout from the cached numbers.
 *
 * The measurement is two hidden layout passes — labels rendered, then labels
 * collapsed — both inside `useLayoutEffect`, so they complete before paint and
 * the full strip never flashes over whatever sits beside it. They re-run only
 * when the tab set changes or the webfont lands, never on resize: resizing
 * changes the space available, not what the tabs need.
 */
export function useTabStripLayout<Key extends string>({
  tabs,
  activeTab,
  moreLabel
}: Args<Key>): Result<Key> {
  const stripRef = useRef<HTMLDivElement>(null)
  const probesRef = useRef<HTMLDivElement>(null)

  const [metrics, setMetrics] = useState<StripMetrics | null>(null)
  const [measureStep, setMeasureStep] = useState<MeasureStep | null>("full")
  const [available, setAvailable] = useState<number | null>(null)

  // A new tab set (permissions changed) invalidates every cached width.
  // Resetting during render rather than in an effect stops the stale metrics
  // being used for a frame.
  const signature = tabs.map((tab) => tab.value).join(",")
  const measuredSignature = useRef(signature)
  if (measuredSignature.current !== signature) {
    measuredSignature.current = signature
    setMetrics(null)
    setMeasureStep("full")
  }

  const partial = useRef<StripMetrics | null>(null)
  useLayoutEffect(() => {
    const strip = stripRef.current
    if (!strip || measureStep === null) return

    // antd owns the button element, so its own class is the only stable handle.
    const buttons = Array.from(
      strip.querySelectorAll<HTMLElement>(".ant-radio-button-wrapper")
    )

    if (measureStep === "full") {
      const style = window.getComputedStyle(strip)
      const moreWidths: Record<string, number> = {}

      probesRef.current
        ?.querySelectorAll<HTMLElement>("[data-more-probe]")
        .forEach((probe) => {
          const label = probe.dataset.moreProbe
          if (label) moreWidths[label] = probe.offsetWidth
        })

      partial.current = {
        fullTabWidths: buttons.map((button) => button.offsetWidth),
        iconTabWidths: [],
        padding:
          (parseFloat(style.paddingLeft) || 0) +
          (parseFloat(style.paddingRight) || 0),
        gap: parseFloat(style.columnGap) || 0,
        moreWidths
      }
      setMeasureStep("icons")
      return
    }

    // The collapsed widths come from this pass: once the strip gives up its
    // labels the "More" menu is holding icons, so it is icon widths that decide
    // how many still fit.
    setMetrics({
      fullTabWidths: partial.current?.fullTabWidths ?? [],
      moreWidths: partial.current?.moreWidths ?? {},
      padding: partial.current?.padding ?? 0,
      gap: partial.current?.gap ?? 0,
      iconTabWidths: buttons.map((button) => button.offsetWidth)
    })
    setMeasureStep(null)
  }, [measureStep, signature])

  // Text metrics change when the webfont swaps in, so widths measured against
  // the fallback face come out a few pixels short and the strip then believes
  // it fits when it does not. Measured on a cold load at a 992px viewport: the
  // strip needed 725px in a 718px box and spilled 7px.
  useLayoutEffect(() => {
    let cancelled = false

    void document.fonts?.ready?.then(() => {
      if (!cancelled) setMeasureStep("full")
    })

    return () => {
      cancelled = true
    }
  }, [])

  // The available width also changes when a neighbouring panel collapses, at a
  // constant viewport — which is why this is observed rather than derived from
  // a media query.
  //
  // Keyed on the signature rather than mounted once. The caller renders nothing
  // until its async permissions resolve, so on the first pass there is no strip
  // to observe; left on `[]` this would never re-run, `available` would stay
  // null, and the layout would fall back to `full` — the overflow the hook
  // exists to prevent.
  useLayoutEffect(() => {
    const strip = stripRef.current
    if (!strip) return

    const read = () => setAvailable(strip.clientWidth)
    read()

    // Without ResizeObserver a neighbouring panel collapsing cannot be seen,
    // but the viewport half still can.
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", read)
      return () => window.removeEventListener("resize", read)
    }

    const observer = new ResizeObserver(read)
    observer.observe(strip)
    return () => observer.disconnect()
  }, [signature])

  const measuring = measureStep !== null

  const layout = useMemo(
    () =>
      resolveStripLayout<Key>({
        tabs,
        activeTab,
        // while measuring, the strip must render at its natural width
        metrics: measuring ? null : metrics,
        available,
        moreLabel
      }),
    [tabs, activeTab, metrics, available, measuring, moreLabel]
  )

  return {
    ...layout,
    stripRef,
    probesRef,
    measuring,
    measuringCollapsed: measureStep === "icons"
  }
}
